import classNames from 'classnames';
import styles from './AcademyDetails.module.scss';
import { Heading, LinkButton, Link, Paragraph } from 'ui/components';
import { SurfaceSection } from 'ui/components/4-habitats/SurfaceSection';
import IconArrowRight from 'ui/icons/icon-arrow-right-web.svg';

export interface AcademyDetailsProps {
	className?: string;
	dictionary?: { [key: string]: string | undefined };
	academyEntry?: Models.AcademyEntry;
	topics?: Models.AcademyEntry['topics'];
	language?: Models.AcademyEntry['language'];
	formattedStartDateTime?: string;
	formattedEndDateTime?: string;
	host?: Models.AcademyEntry['host'];
	location?: Models.AcademyEntry['location'];
	googleMapsUrl?: string;
	contactInformation?: Models.AcademyEntry['contactInfoLink'];
	linkToSignUp?: { url: string; name?: string; target?: string };
	signUpAriaLabel?: string;
	additionalInfoFiles?: Models.AcademyEntry['additionalInfoFiles'];
	additionalInfoLink?: Models.AcademyEntry['additionalInfoLink'];
}

export const AcademyDetails: React.FC<AcademyDetailsProps> = ({
	className,
	dictionary,
	academyEntry,
	topics,
	language,
	formattedStartDateTime,
	formattedEndDateTime,
	location,
	googleMapsUrl,
	host,
	contactInformation,
	linkToSignUp,
	signUpAriaLabel,
	additionalInfoFiles,
	additionalInfoLink,
}) => {
	return (
		<SurfaceSection className={classNames(styles.AcademyDetails, className)}>
			<article className={styles.AcademyDetails_container}>
				{/* Column 1 */}
				<section className={styles.AcademyDetails_informationCol}>
					<Heading headingLevel="h5" style="headlineXs" className={styles.AcademyDetails_columnTitle}>
						{dictionary?.courseTitle}
					</Heading>
					<div className={styles.AcademyDetails_metaDetails}>
						<div>
							<Heading headingLevel="h6" className={styles.AcademyDetails_metaDetailsHeading}>
								{dictionary?.topicTitle}
							</Heading>
							{topics?.length > 0 &&
								topics.map((topic, index) => (
									<p key={index} className={styles.AcademyDetails_metaDetailsText}>
										{topic?.name}
									</p>
								))}
						</div>
						<div>
							<Heading headingLevel="h6" className={styles.AcademyDetails_metaDetailsHeading}>
								{dictionary?.languageTitle}
							</Heading>
							<p className={styles.AcademyDetails_metaDetailsText}>{language?.name}</p>
						</div>
					</div>
				</section>
				{/* Column 2 */}
				<section className={styles.AcademyDetails_informationCol}>
					<Heading headingLevel="h5" style="headlineXs" className={styles.AcademyDetails_columnTitle}>
						{dictionary?.timePlaceTitle}
					</Heading>
					<div className={styles.AcademyDetails_metaDetails}>
						<div>
							<Heading headingLevel="h6" className={styles.AcademyDetails_metaDetailsHeading}>
								{dictionary?.startTimeTitle}
							</Heading>
							<p className={styles.AcademyDetails_metaDetailsText}>
								<time dateTime={academyEntry?.startDateTime}>{formattedStartDateTime}</time>
							</p>
						</div>
						<div>
							<Heading headingLevel="h6" className={styles.AcademyDetails_metaDetailsHeading}>
								{dictionary?.endTimeTitle}
							</Heading>
							<p className={styles.AcademyDetails_metaDetailsText}>
								<time dateTime={academyEntry?.endDateTime}>{formattedEndDateTime}</time>
							</p>
						</div>
						<div className={styles.AcademyDetails_metaLocationDetails}>
							{location?.address && (
								<>
									<Heading headingLevel="h6" className={styles.AcademyDetails_metaDetailsHeading}>
										{dictionary?.locationTitle}
									</Heading>
									<Paragraph variant="website" style="md" className={styles.AcademyDetails_metaDetailsText}>
										{location.address}
									</Paragraph>
								</>
							)}
						</div>
						{location && googleMapsUrl && location?.longitude && location?.latitude && (
							<LinkButton style="secondaryWeb" title={location?.name} ariaLabel={location?.name} url={googleMapsUrl} target="_blank">
								{dictionary?.locationLinkButtonText || location?.name}
							</LinkButton>
						)}
					</div>
				</section>
				{/* Column 3 */}
				<section className={styles.AcademyDetails_informationCol}>
					<Heading headingLevel="h5" style="headlineXs" className={styles.AcademyDetails_columnTitle}>
						{dictionary?.additionalInfoTitle}
					</Heading>
					{(additionalInfoFiles || additionalInfoLink) && (
						<div className={classNames(styles.AcademyDetails_metaDetails)}>
							<div className={styles.AcademyDetails_metaDetailsInner}>
								{additionalInfoFiles?.length > 0 &&
									additionalInfoFiles.map((file, index) => (
										<Link
											key={index}
											ariaLabel={`${dictionary?.additionalInfoFilesLinkText} ${file?.name}`}
											url={file?.url}
											variant="website"
											style="underline"
										>
											{dictionary?.additionalInfoFilesLinkText} {file?.name}
										</Link>
									))}
								{additionalInfoLink?.url && (
									<Link
										ariaLabel={`${dictionary?.additionalInfoLinkText} ${additionalInfoLink?.name}`}
										url={additionalInfoLink?.url}
										variant="website"
										style="underline"
									>
										{additionalInfoLink?.name}
									</Link>
								)}
							</div>
						</div>
					)}
					<div
						className={classNames(styles.AcademyDetails_metaDetails, {
							[styles.AcademyDetails_metaDetails___noTopSpacing]: additionalInfoFiles || additionalInfoLink,
						})}
					>
						{(contactInformation || host?.name || host?.link?.url) && (
							<div className={styles.AcademyDetails_metaDetailsInner}>
								<Heading headingLevel="h6" className={styles.AcademyDetails_metaDetailsHeading}>
									{dictionary?.contactInformationTitle}
								</Heading>
								{host?.name && (
									<Paragraph variant="website" style="md" className={styles.AcademyDetails_metaDetailsText}>
										{dictionary?.hostText} {host?.name}
									</Paragraph>
								)}
								{host?.link?.url && (
									<Link
										ariaLabel={`${dictionary?.contactHostLinkText} ${host?.name}`}
										url={host?.link?.url}
										variant="website"
										style="underline"
									>
										{dictionary?.contactHostLinkText} {host?.name}
									</Link>
								)}
								{contactInformation && (
									<Link
										ariaLabel={`${dictionary?.contactLinkText} ${contactInformation?.name}`}
										url={contactInformation?.url}
										variant="website"
										style="underline"
									>
										{dictionary?.contactLinkText || contactInformation?.name}
									</Link>
								)}
							</div>
						)}
					</div>
					{linkToSignUp && (
						<LinkButton
							style="primaryWeb"
							title={signUpAriaLabel || linkToSignUp?.name}
							ariaLabel={signUpAriaLabel || linkToSignUp?.name}
							url={linkToSignUp?.url}
							target={linkToSignUp?.target}
							withBackIcon={<IconArrowRight />}
						>
							{linkToSignUp?.name}
						</LinkButton>
					)}
				</section>
			</article>
		</SurfaceSection>
	);
};
